import React, { FC } from 'react';
import {
  SelectInput,
  SelectArrayInput,
  NumberInput,
  useTranslate,
  TextInput,
  ArrayInput,
  SimpleFormIterator,
} from 'react-admin';
import {
  AutocompleteArrayTextInput,
  AmountInput,
  CurrencySelect,
  DateInput,
} from '~/components/ra';
import { required } from 'react-admin';
import { BooleanInput } from 'react-admin';
import { H3, H4 } from '~/components';
import { NumberEx } from '~/utils';
import { useExternalSourceTypes } from '~/hooks/externalSources';
import { useProductsBySolutionId } from '~/hooks/product';
import { useTenantAccounts } from '~/hooks/tenant';
import { useFormState } from 'react-final-form';
import { l } from '~/utils/solutions';
import { useStyles } from '~/hooks/useSolutionsStyles';
import {
  SelectTransactionType,
  REWARD_EXCLUDED_OPERATION_TYPES,
} from '~/components/TransactionsHistory/components/SelectTransactionType';
import Alert from '@material-ui/lab/Alert';
import { get } from 'lodash';
import { Solution } from '~/types';
import { useForm } from 'react-final-form';
import cx from 'classnames';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import { useHandbooksQuery } from '~/hooks/useHandbook';
import { Configuration } from '~/api/solutions';

interface RewardsFormProps {
  record: Solution;
  configurationRecord: Configuration;
  keyName: string;
  solutionId: string;
}

export const RewardsForm: FC<RewardsFormProps> = ({
  record,
  configurationRecord,
  keyName,
  solutionId,
}) => {
  const classes = useStyles();
  const { values } = useFormState();
  const form = useForm();
  const translate = useTranslate();
  const t = (key: string, args?: any): string => translate(l(`featuresConfig.${key}`), args);

  const enableRewards = values?.[`${keyName}`]?.enableRewards;
  const enableOnboardingRewards = values?.[`${keyName}`]?.onboardingReward?.enabled;
  const enableFirstTransactionReward = values?.[`${keyName}`]?.firstTransactionReward?.enabled;

  const {
    data: externalSourceTypesData,
    isLoading: externalSourceTypesLoading,
  } = useExternalSourceTypes();
  const externalSourceTypes = externalSourceTypesLoading
    ? []
    : externalSourceTypesData?.map((type) => ({
        id: `EXTERNAL_SOURCE:${type}`,
        name: type,
      }));
  const { data: inviterProductsData, isLoading: inviterProductsLoading } = useProductsBySolutionId(
    solutionId
  );
  const inviterProducts = inviterProductsLoading
    ? []
    : inviterProductsData?.map((product) => ({
        id: `ACCOUNT:${product?.id}`,
        name: product?.name,
      }));

  const inviterOptions = [...(externalSourceTypes || []), ...(inviterProducts || [])];

  const { data: tenantAccountsData, isLoading: tenantAccountsDataLoading } = useTenantAccounts();
  const tenantAccounts = tenantAccountsDataLoading
    ? []
    : tenantAccountsData?.map((account) => ({
        id: account?.id,
        name: `${account?.alias} - ${account?.number}`,
      }));
  const withIndividualInvitation = record?.features?.includes('veengu_individual_invitation');
  const inviteeSolution = configurationRecord?.veengu_individual_invitation?.solutionId;

  const { data: inviteeProductsData, isLoading: inviteeProductsLoading } = useProductsBySolutionId(
    inviteeSolution
  );
  const inviteeProducts = inviteeProductsLoading
    ? []
    : inviteeProductsData?.map((product) => ({
        id: `ACCOUNT:${product?.id}`,
        name: product?.name,
      }));

  const inviteeOptions = [...(externalSourceTypes || []), ...(inviteeProducts || [])];

  const { data: inviteeHandbook, isLoading: inviteeHandbookLoading } = useHandbooksQuery(
    inviteeSolution
  );
  const inviteeCurrency = inviteeHandbookLoading
    ? []
    : inviteeHandbook?.currencies?.map((currency) => ({
        id: currency?.id,
        name: currency?.name,
      }));

  // Reset unknown values in inviteePaymentInstruments
  // useEffect(() => {
  //   if (!inviteeProductsLoading && !externalSourceTypesLoading) {
  //     const currentValues = values?.[`${keyName}`]?.inviteePaymentInstruments || [];
  //     if (Array.isArray(currentValues) && currentValues.length > 0) {
  //       const validOptionIds = inviteeOptions.map((option) => option.id);
  //       const validValues = currentValues.filter((value: any) => validOptionIds.includes(value));

  //       // Only update if there's a difference to avoid unnecessary form updates
  //       if (validValues.length !== currentValues.length) {
  //         form.change(`${keyName}.inviteePaymentInstruments`, validValues);
  //       }
  //     }
  //   }
  // }, [inviteeOptions, values, form, keyName, inviteeProductsLoading, externalSourceTypesLoading]);

  const isInviteeFeatureMissing = !withIndividualInvitation || !inviteeSolution;

  const isInviteeCurrencyDisabled = isInviteeFeatureMissing || inviteeHandbookLoading;

  const validateInviteePaymentInstruments = (value: any) => {
    if (isInviteeFeatureMissing) {
      return t('isInviteeFeatureMissingError');
    }
    if (!value || !Array.isArray(value)) return undefined;

    const validOptionIds = inviteeOptions.map((option) => option.id);
    const invalidValues = value.filter((item) => !validOptionIds.includes(item));

    if (invalidValues.length > 0) {
      return t('invalidInviteePaymentInstruments');
    }

    return undefined;
  };

  const validateInviteeCurrency = (value: any) => {
    if (isInviteeFeatureMissing) {
      return t('isInviteeFeatureMissingError');
    }
    if (!value) return undefined;
    if (inviteeHandbookLoading) return undefined;
    const validCurrencies = inviteeCurrency?.map((currency) => currency.id) || [];
    if (!validCurrencies.includes(value)) {
      return t('invalidInviteeRewardCurrency');
    }
    return undefined;
  };
  return (
    <>
      {(!withIndividualInvitation || !inviteeSolution) && (
        <Alert className={classes.marginBottom} severity='error'>
          {t('featureIsNotSetWarning')}
        </Alert>
      )}
      <BooleanInput
        label={t('enableRewards')}
        source={`${keyName}.enableRewards`}
        validate={required()}
        className={classes.inputWidth}
      />
      {enableRewards && (
        <>
          <SelectInput
            label={t('tenantAccountRewards')}
            source={`${keyName}.tenantAccount`}
            validate={required()}
            choices={tenantAccounts}
            className={classes.inputWidth}
          />
          <SelectArrayInput
            label={t('inviterPaymentInstruments')}
            source={`${keyName}.inviterPaymentInstruments`}
            choices={inviterOptions}
            validate={required()}
            loading={externalSourceTypesLoading || inviterProductsLoading}
            className={classes.inputWidth}
          />
          <div className={classes.inputWidth}>
            <div className={classes.selectWrapper}>
              <SelectArrayInput
                label={t('inviteePaymentInstruments')}
                source={`${keyName}.inviteePaymentInstruments`}
                choices={inviteeOptions}
                validate={[required(), validateInviteePaymentInstruments]}
                loading={externalSourceTypesLoading || inviteeProductsLoading}
                helperText={t('inviteePaymentInstrumentsHint')}
                disabled={isInviteeFeatureMissing}
                optionText={(choice: any) => {
                  // To handle unknown values which are not in the options
                  return choice?.name ?? t('unknownOption');
                }}
              />
              {/* Not show clear when select is disabled */}
              {!isInviteeFeatureMissing && (
                <IconButton
                  onClick={() => {
                    if (form.mutators && form.mutators.setFieldTouched) {
                      form.mutators.setFieldTouched(`${keyName}.inviteePaymentInstruments`, false);
                    }
                    form.change(`${keyName}.inviteePaymentInstruments`, []);
                  }}
                  className={classes.clearButton}
                >
                  <CloseIcon className={classes.clearButtonIcon} />
                </IconButton>
              )}
            </div>
          </div>
          <CurrencySelect
            label={t('inviterRewardCurrency')}
            source={`${keyName}.inviterRewardCurrency`}
            solutionId={record?.id} // Inviter solution
            validate={required()}
            record={values}
            className={classes.inputWidth}
          />
          <CurrencySelect
            label={t('inviteeRewardCurrency')}
            source={`${keyName}.inviteeRewardCurrency`}
            solutionId={inviteeSolution} // Invitee solution
            validate={[required(), validateInviteeCurrency]}
            record={values}
            className={classes.inputWidth}
            disabled={isInviteeCurrencyDisabled}
          />
          <H3>{t('onboardingRewards')}</H3>
          <BooleanInput
            label={t('enabled')}
            source={`${keyName}.onboardingReward.enabled`}
            className={classes.inputWidth}
          />
          {enableOnboardingRewards && (
            <>
              <H4>{t('rewardForInviter')}</H4>
              <NumberInput
                label={t('amountForInviter')}
                source={`${keyName}.onboardingReward.inviterRewards.amount`}
                parse={NumberEx.positive}
                min={0}
                className={classes.inputWidth}
              />
              <NumberInput
                label={t('processingDelayHours')}
                source={`${keyName}.onboardingReward.inviterRewards.processingDelayHours`}
                min={0}
                step={1}
                className={classes.inputWidth}
              />
              <H4>{t('rewardForInvitee')}</H4>
              <NumberInput
                label={t('amountForInvitee')}
                source={`${keyName}.onboardingReward.inviteeRewards.amount`}
                parse={NumberEx.positive}
                min={0}
                className={classes.inputWidth}
              />
              <NumberInput
                label={t('processingDelayHours')}
                source={`${keyName}.onboardingReward.inviteeRewards.processingDelayHours`}
                min={0}
                step={1}
                className={classes.inputWidth}
              />
            </>
          )}
          <H3>{t('firstTransactionReward')}</H3>
          <BooleanInput
            label={t('enabled')}
            source={`${keyName}.firstTransactionReward.enabled`}
            className={classes.inputWidth}
          />
          {enableFirstTransactionReward && (
            <>
              <DateInput
                label={t('maxInviteeOnboardingDate')}
                source={`${keyName}.firstTransactionReward.maxInviteeOnboardingDate`}
                options={{ clearable: true }}
                className={classes.inputWidth}
              />
              <H4>{t('firstTransactionConditions')}</H4>
              <SelectInput
                label={t('direction')}
                defaultValue='ANY'
                source={`${keyName}.firstTransactionReward.transaction.direction`}
                choices={[
                  { id: 'ANY', name: t('any') },
                  { id: 'DEBIT', name: t('debit') },
                  { id: 'CREDIT', name: t('credit') },
                ]}
                className={classes.inputWidth}
              />
              <AutocompleteArrayTextInput
                label={t('transactionTags')}
                source={`${keyName}.firstTransactionReward.transaction.tags`}
                freeSolo
                record={values}
                regularHelperText={t('tagsHelperText')}
                className={classes.inputWidth}
              />
              <H4 topSpacing={3}>{t('minAmount')}</H4>
              <Alert className={cx(classes.marginBottom, classes.marginTop)} severity='warning'>
                {t('minAmountAlert')}
              </Alert>
              <div className={classes.inputWidth}>
                <AmountInput
                  amountLabel={t('amountGreaterThan')}
                  source={`${keyName}.firstTransactionReward.transaction.minAmount`}
                  positiveOnly
                  solutionId={record.id}
                  shouldClearOnUnmount={false}
                  allowEmpty
                />
              </div>
              <H4>{t('rewardForInviter')}</H4>
              <NumberInput
                label={t('amountForInviter')}
                source={`${keyName}.firstTransactionReward.inviterRewards.amount`}
                parse={NumberEx.positive}
                min={0}
                className={classes.inputWidth}
              />
              <NumberInput
                label={t('processingDelayHours')}
                source={`${keyName}.firstTransactionReward.inviterRewards.processingDelayHours`}
                min={0}
                step={1}
                className={classes.inputWidth}
              />
              <H4>{t('rewardForInvitee')}</H4>
              <NumberInput
                label={t('amountForInvitee')}
                source={`${keyName}.firstTransactionReward.inviteeRewards.amount`}
                parse={NumberEx.positive}
                min={0}
                className={classes.inputWidth}
              />
              <NumberInput
                label={t('processingDelayHours')}
                source={`${keyName}.firstTransactionReward.inviteeRewards.processingDelayHours`}
                min={0}
                step={1}
                className={classes.inputWidth}
              />
              <H4>{t('operationTypes')}</H4>
              <OperationTypesFieldArray
                source={`${keyName}.firstTransactionReward.transaction.operationTypes`}
              />
            </>
          )}
        </>
      )}
    </>
  );
};

interface OperationTypesFieldArrayProps {
  source: string;
}

const OperationTypesFieldArray: FC<OperationTypesFieldArrayProps> = ({ source }) => {
  const classes = useStyles();
  const translate = useTranslate();
  const t = (key: string): string => translate(l(`featuresConfig.${key}`));

  const validateSelectInput = (value: any, allValues: any) => {
    const parentArray = get(allValues, source) || [];
    const currentIndex = parentArray.findIndex((item: any) => item?.operationType === value);
    if (currentIndex !== -1 && parentArray[currentIndex]?.serviceIdOrSubtype) {
      if (!value) {
        return t('operationTypeRequired');
      }
    }
    return undefined;
  };
  return (
    <ArrayInput source={source} label='' className={classes.simpleArrayInput}>
      <SimpleFormIterator>
        <SelectInput
          label={t('operationType')}
          source='operationType'
          choices={
            (SelectTransactionType.getChoices(
              translate,
              true,
              REWARD_EXCLUDED_OPERATION_TYPES
            ) as any[]).filter((item) => !!item.id) || []
          }
          fullWidth
          allowEmpty
          validate={validateSelectInput}
        />
        <TextInput label={t('serviceIdOrSubtype')} source='serviceIdOrSubtype' fullWidth />
      </SimpleFormIterator>
    </ArrayInput>
  );
};
